import { FormBuilder } from '@/components/formBuilder';
import { useEmailField } from '@/components/formBuilder/formFields/useEmailField';
import { templatesCategoryConfig } from '@/pages/settings/ats/templates-settings/constants';
import { useGetTemplatesVariablesQuery } from '@/store/query/email-templates-settings';
import { getInvalidVariableAlertConfig } from '@/utils/getInvalidVariableAlertConfig';
import { Box } from '@chakra-ui/react';
import { ChooseSurveyTemplate } from './ChooseSurveyTemplate';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectUsers } from '@/store/users/selectors';
import { ScheduleForLater } from '@/components/ats/job-stage-actions/actions/assessment/send-assessment/schedule/ScheduleForLater';
import { selectCurrUser } from '@/store/bootstrap/selectors';

const getSurveyEmailInitialValue = (
  activity,
  selectedTemplate,
  variablesListArr,
  currUser
) => {
  const {
    candidate_comm_sender = undefined,
    cc_participants,
    bcc_participants,
    candidate_comm_template,
    ats_shared_email = undefined,
    to_participants = undefined,
  } = activity;
  let emailPayload = {
    from: ats_shared_email?.id || candidate_comm_sender?.id || currUser?.id,
    fromUserObject: ats_shared_email?.id
      ? { ...ats_shared_email, isSharedEmail: true }
      : candidate_comm_sender || currUser,
    cc: cc_participants?.map((item) => item?.id),
    bcc: bcc_participants?.map((item) => item?.id),
    toAddress: to_participants?.map((item) => item?.id),
    variablesListArr,
  };

  if (!activity?.id || selectedTemplate?.id) {
    emailPayload['subject'] = selectedTemplate?.subject || '';
    emailPayload['body'] = selectedTemplate?.body || '';
    emailPayload['attachments'] = selectedTemplate?.attachments || [];
  } else {
    const { subject, body, attachments } = candidate_comm_template || {};
    emailPayload['subject'] = subject;
    emailPayload['body'] = body;
    emailPayload['attachments'] = attachments;
  }
  return emailPayload;
};

interface SurveyFormEditorProps {
  formRef?: any;
  selectedTemplate?: any;
  handleFormSubmit?: () => void;
  selectOnChange?: () => void;
  activity?: any;
  surveyType?: string;
  isScheduled?: boolean;
  isInterviewPlan?: boolean;
  scheduling?: {
    scheduleRef: any;
    handleSchedule: (scheduleValues: any) => void;
    handleCancelSchedule: () => void;
    isScheduling: boolean;
    isCanceling: boolean;
  };
}

export const SurveyFormEditor: React.FC<SurveyFormEditorProps> = ({
  formRef = null,
  selectedTemplate: selectedTemplateProp = {},
  handleFormSubmit = () => {},
  selectOnChange = () => {},
  activity = {},
  surveyType = '',
  isScheduled = false,
  isInterviewPlan = false,
  scheduling = {
    scheduleRef: null,
    handleSchedule: () => {},
    handleCancelSchedule: () => {},
    isScheduling: false,
    isCanceling: false,
  },
}) => {
  // Derive effective template: use prop if available, otherwise fallback to activity data
  // Note: Activity object uses candidate_comm_template (mapped from API's ats_comm_template)
  const selectedTemplate = selectedTemplateProp?.id
    ? selectedTemplateProp
    : (activity?.candidate_comm_template || {});
  const view = useMemo(() => {
    const baseView = templatesCategoryConfig.survey_notice.view;
    if (surveyType === 'employee') {
      return baseView;
    }
    return baseView.filter((item) => item !== 'user');
  }, [surveyType]);

  const { data: { variables: rawVariables = {} } = {} } =
    useGetTemplatesVariablesQuery({
      params: { view },
    });

  // Filter out user.email_signature from variables for survey context
  const variables = useMemo(() => {
    const filtered = { ...rawVariables };
    if (filtered.user && Array.isArray(filtered.user)) {
      filtered.user = filtered.user.filter(
        (variable: string) => variable !== 'user.email_signature'
      );
    }
    return filtered;
  }, [rawVariables]);

  const variablesListArr = Object.entries(variables || {}).flatMap(
    ([, values = []]) => values
  );

  const users = useSelector(selectUsers);
  const currUser = useSelector(selectCurrUser);
  // include ToAddress field when survey type is employee
  const toAddress = useMemo(() => {
    if (surveyType === 'employee') {
      return users.map((user) => ({
        email: user.email,
        name: user.name,
        id: user.id,
        avatar: user.avatar,
      }));
    }
    return [];
  }, [surveyType, users]);

  const emailFieldConfigs = useEmailField({
    formRef,
    templateData: selectedTemplate,
    disableFrom: false,
    valueKey: 'id',
    variables,
    showInvalidVariableAlert: false,
    toAddress,
    ToAddressMultiSelect: true,
    showFromMandatoryIcon: !isInterviewPlan, // From is mandatory when NOT in interview plan (i.e., sending survey)
    showToMandatoryIcon: !isInterviewPlan, // To is mandatory when NOT in interview plan (i.e., sending survey)
  });

  // Get default template names based on survey type (array of possible names to match)
  const initialTemplateNames =
    surveyType === 'candidate'
      ? [
          'Candidate Survey Template',
          'Candidate survey email',
          'Candidate survey email template',
          'Candidate survey ',
          'Candidate survey template',
        ]
      : [
          'HM Survey Template',
          'HM survey email',
          'HM survey email template',
          'HM survey ',
          'Hiring Manager Survey Template',
        ];

  // In edit mode (activity.id exists), don't auto-select initial template
  const isEditMode = !!activity?.id;

  const surveyEmailFormConfig = [
    {
      type: 'custom',
      Field: ChooseSurveyTemplate,
      props: {
        selectOnChange,
        styleVersion: 'v2',
        category: ['survey_notice'],
        initialValue: isEditMode ? null : initialTemplateNames,
        surveyType: surveyType,
        selectedTemplate: selectedTemplate,
      },
    },
    {
      id: 'emailFormConfig',
      type: 'wrapper',
      fields: emailFieldConfigs,
      Wrapper: Box,
      wrapperProps: {
        border: '1px solid',
        borderColor: 'border-color',
        borderRadius: 'sm',
        marginTop: '12px',
      },
    },
    getInvalidVariableAlertConfig({
      variablesListArr,
      isBodySubject: true,
    }),
  ];

  return (
    <Box px="24px" pt="16px">
      <FormBuilder
        key={selectedTemplate?.id}
        initialValues={getSurveyEmailInitialValue(
          activity,
          selectedTemplate,
          variablesListArr,
          currUser
        )}
        config={surveyEmailFormConfig}
        onSubmit={handleFormSubmit}
        formRef={formRef}
      />
      {isScheduled && (
        <Box mb="80px">
          <ScheduleForLater
            scheduleRef={scheduling.scheduleRef}
            scheduleDetails={activity}
            onSchedule={(values) => scheduling.handleSchedule(values)}
            onCancelSchedule={scheduling.handleCancelSchedule}
            isScheduling={scheduling.isScheduling}
            isCanceling={scheduling.isCanceling}
            isScheduled={true}
            styles={{
              margin: '8px 0px !important',
            }}
          />
        </Box>
      )}
    </Box>
  );
};
